import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_PAGE_SIZE = 50;

/**
 * Server-side paginated list loader.
 * fetcher({ page, pageSize, search, filters, ...extra }) => { rows, total }
 */
export function useServerPagedList({
  fetcher,
  pageSize = DEFAULT_PAGE_SIZE,
  search = '',
  extra = {},
  filters,
  enabled = true,
}) {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(String(search || '').trim());
  const [debouncedFilters, setDebouncedFilters] = useState(filters || {});
  const requestIdRef = useRef(0);
  const extraKey = JSON.stringify(extra || {});
  const filtersKey = JSON.stringify(filters || {});
  const debouncedFiltersKey = JSON.stringify(debouncedFilters || {});

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(String(search || '').trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters || {}), 300);
    return () => clearTimeout(timer);
  }, [filtersKey]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, extraKey, debouncedFiltersKey]);

  const load = useCallback(async () => {
    if (!enabled) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    const reqId = ++requestIdRef.current;
    try {
      setError('');
      setLoading(true);
      const result = await fetcher({
        page,
        pageSize,
        search: debouncedSearch,
        filters: debouncedFilters,
        ...(extra || {}),
      });
      if (reqId !== requestIdRef.current) return;
      setRows(result?.rows || []);
      setTotal(Number(result?.total) || 0);
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      setRows([]);
      setTotal(0);
      setError(err.message || 'Failed to load data');
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [enabled, fetcher, page, pageSize, debouncedSearch, extraKey, debouncedFiltersKey]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return {
    rows,
    setRows,
    total,
    page,
    setPage,
    pageSize,
    totalPages,
    from,
    to,
    loading,
    error,
    setError,
    debouncedSearch,
    reload: load,
    nextPage: () => setPage((p) => Math.min(totalPages, p + 1)),
    prevPage: () => setPage((p) => Math.max(1, p - 1)),
  };
}
